// /app/frontend/src/pages/risk/dpia/steps/OutcomeStep.jsx
import React, { useMemo, useEffect } from 'react';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Card, CardContent } from '../../../../components/ui/card';
import { Check, AlertTriangle } from 'lucide-react';
import { getRiskLevel, getRiskBadgeColor, RISK_LEVELS } from '../constants';

export default function OutcomeStep({ formData, updateField }) {
  // Auto-calculate project risk from risks array
  const calculatedMetrics = useMemo(() => {
    const risks = formData.risks || [];
    if (risks.length === 0) {
      return {
        initialRisk: 0,
        afterControls: 0,
        pointsReduced: 0,
        riskReduction: 0,
        riskCount: 0
      };
    }

    const totalBefore = risks.reduce((sum, r) => sum + (r.score_before || 0), 0);
    const totalAfter = risks.reduce((sum, r) => sum + (r.score_after || 0), 0);
    const avgBefore = totalBefore / risks.length;
    const avgAfter = totalAfter / risks.length;
    const pointsReduced = avgBefore - avgAfter;
    const riskReduction = avgBefore > 0 ? ((avgBefore - avgAfter) / avgBefore * 100) : 0;

    return {
      initialRisk: avgBefore,
      afterControls: avgAfter,
      pointsReduced,
      riskReduction,
      riskCount: risks.length
    };
  }, [formData.risks]);

  // Auto-update form data when metrics change
  useEffect(() => {
    if (calculatedMetrics.riskCount > 0) {
      const updates = {};
      if (Math.abs((formData.initial_project_risk || 0) - calculatedMetrics.initialRisk) > 0.01) {
        updates.initial_project_risk = Math.round(calculatedMetrics.initialRisk * 10) / 10;
      }
      if (Math.abs((formData.project_risk_after_controls || 0) - calculatedMetrics.afterControls) > 0.01) {
        updates.project_risk_after_controls = Math.round(calculatedMetrics.afterControls * 10) / 10;
      }
      if (Math.abs((formData.risk_reduction_points || 0) - calculatedMetrics.pointsReduced) > 0.01) {
        updates.risk_reduction_points = Math.round(calculatedMetrics.pointsReduced * 10) / 10;
      }
      if (Math.abs((formData.risk_reduction_percent || 0) - calculatedMetrics.riskReduction) > 0.1) {
        updates.risk_reduction_percent = Math.round(calculatedMetrics.riskReduction * 10) / 10;
      }
      
      // Batch update if there are changes
      if (Object.keys(updates).length > 0) {
        Object.entries(updates).forEach(([key, value]) => {
          updateField(key, value);
        });
      }
    }
  }, [calculatedMetrics, formData.initial_project_risk, formData.project_risk_after_controls, formData.risk_reduction_points, formData.risk_reduction_percent, updateField]);

  const riskLevel = getRiskLevel(calculatedMetrics.afterControls);

  // Get recommendation based on risk level
  const getRecommendation = (level) => {
    switch(level) {
      case 'low': return 'Processing may proceed with standard safeguards.';
      case 'moderate': return 'Processing may proceed with enhanced monitoring.';
      case 'high': return 'Consider consulting with supervisory authority before proceeding.';
      case 'extreme': return 'Processing should not proceed without supervisory authority consultation.';
      default: return 'Add risks to see recommendation.';
    }
  };

  return (
    <div className="space-y-6">
      {/* Assessment Outcome Header */}
      <div className="text-center mb-2">
        <div className="w-full h-1 bg-gradient-to-r from-teal-500 via-teal-600 to-teal-500 rounded-full max-w-2xl mx-auto" />
      </div>

      {/* 4-Box Metrics Display */}
      {calculatedMetrics.riskCount === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="p-8 text-center text-slate-400">
              <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-lg">No risks have been added yet.</p>
              <p className="text-sm mt-1">Add risks in the Risks step to see the assessment outcome.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-4 gap-0 rounded-lg overflow-hidden shadow-md" data-testid="outcome-metrics">
          {/* Initial Risk - Orange */}
          <div className="bg-orange-500 py-4 px-3 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-white mb-1">
              {calculatedMetrics.initialRisk.toFixed(1)}
            </span>
            <span className="text-white/90 text-xs font-medium tracking-wide uppercase">
              Initial Risk
            </span>
          </div>

          {/* After Controls - Green */}
          <div className="bg-green-600 py-4 px-3 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-white mb-1">
              {calculatedMetrics.afterControls.toFixed(1)}
            </span>
            <span className="text-white/90 text-xs font-medium tracking-wide uppercase">
              After Controls
            </span>
          </div>

          {/* Points Reduced - Dark Blue */}
          <div className="bg-slate-700 py-4 px-3 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-white mb-1">
              {calculatedMetrics.pointsReduced.toFixed(1)}
            </span>
            <span className="text-white/90 text-xs font-medium tracking-wide uppercase">
              Points Reduced
            </span>
          </div>

          {/* Risk Reduction % - Teal */}
          <div className="bg-teal-600 py-4 px-3 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-white mb-1">
              {calculatedMetrics.riskReduction.toFixed(1)}%
            </span>
            <span className="text-white/90 text-xs font-medium tracking-wide uppercase">
              Risk Reduction
            </span>
          </div>
        </div>
      )}

      {/* Recommendation */}
      {calculatedMetrics.riskCount > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-4">
            <p className="text-sm text-slate-400 mb-2">Recommendation</p>
            <p className="text-white text-lg">{getRecommendation(riskLevel)}</p>
          </CardContent>
        </Card>
      )}

      {/* Risk Scale Reference */}
      {calculatedMetrics.riskCount > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-4">
            <p className="text-sm text-slate-400 mb-3">Risk Scale (Impact × Likelihood)</p>
            <div className="flex gap-2">
              {Object.entries(RISK_LEVELS).map(([key, level]) => (
                <div 
                  key={key} 
                  className={`flex-1 p-2 rounded text-center text-xs ${level.color} text-white ${
                    riskLevel === key ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-800' : 'opacity-75'
                  }`}
                >
                  {level.label} ({level.range})
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* DPO Advice Section */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Summary of DPO Advice</Label>
          <Textarea 
            value={formData.dpo_advice_summary || ''}
            onChange={(e) => updateField('dpo_advice_summary', e.target.value)}
            placeholder="Enter DPO advice and recommendations..."
            rows={4}
            data-testid="dpo-advice-textarea"
          />
        </div>

        <div className="space-y-3">
          <Label>DPO has been consulted</Label>
          <div className="flex gap-4">
            <div 
              onClick={() => updateField('dpo_consulted', true)}
              className={`
                flex-1 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center
                ${formData.dpo_consulted === true 
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' 
                  : 'border-border hover:border-teal-300'}
              `}
              data-testid="dpo-consulted-yes"
            >
              <div className="flex items-center justify-center gap-2">
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center
                  ${formData.dpo_consulted === true ? 'border-teal-500 bg-teal-500' : 'border-muted-foreground/50'}
                `}>
                  {formData.dpo_consulted === true && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className={`font-medium ${formData.dpo_consulted === true ? 'text-teal-700 dark:text-teal-300' : ''}`}>Yes</span>
              </div>
            </div>
            <div 
              onClick={() => updateField('dpo_consulted', false)}
              className={`
                flex-1 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center
                ${formData.dpo_consulted === false 
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' 
                  : 'border-border hover:border-teal-300'}
              `}
              data-testid="dpo-consulted-no"
            >
              <div className="flex items-center justify-center gap-2">
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center
                  ${formData.dpo_consulted === false ? 'border-teal-500 bg-teal-500' : 'border-muted-foreground/50'}
                `}>
                  {formData.dpo_consulted === false && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className={`font-medium ${formData.dpo_consulted === false ? 'text-teal-700 dark:text-teal-300' : ''}`}>No</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
